import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import SafeImage from "../../../layout/reusable/SafeImage";
import {getBlogs} from "../../../utils/cms.utils";
import {Pagination} from "./pagination";


const truncateHtmlText = (html = "", limit = 100) => {
    const text = html.replace(/<[^>]*>/g, ""); // strip HTML tags
    return text.length > limit
        ? text.slice(0, limit).trim() + "..."
        : text;
};


const BlogStandardMain = ({position}) => {
    const BLOG_PER_PAGE = 3;

    const [blogData, setBlogData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState(null);

    /* =========================
       FETCH FROM CMS (CACHE)
    ========================== */
    useEffect(() => {
        const blogs = getBlogs();
        const items = Array.isArray(blogs) ? blogs : [];

        // optional: sort by newest first
        const sorted = [...items].sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );

        setBlogData(sorted);
    }, []);

    /* =========================
       CATEGORIES (TAGS)
    ========================== */
    const categories = useMemo(() => {
        const set = new Set();
        blogData.forEach((item) => {
            item.tags?.forEach((tag) => set.add(tag));
        });
        return Array.from(set);
    }, [blogData]);

    /* =========================
       FILTER LOGIC
    ========================== */
    const filteredBlogs = useMemo(() => {
        return blogData.filter((item) => {
            const title = item.title || "";
            const desc = item.description || "";

            const matchSearch =
                title.toLowerCase().includes(search.toLowerCase()) ||
                desc.toLowerCase().includes(search.toLowerCase());

            const matchCategory = activeCategory
                ? item.tags?.includes(activeCategory)
                : true;

            return matchSearch && matchCategory;
        });
    }, [blogData, search, activeCategory]);

    /* =========================
       PAGINATION
    ========================== */
    const totalPages = Math.ceil(filteredBlogs.length / BLOG_PER_PAGE);
    const startIndex = currentPage * BLOG_PER_PAGE;

    const currentBlogItems = filteredBlogs.slice(
        startIndex,
        startIndex + BLOG_PER_PAGE
    );

    /* =========================
       RECENT BLOGS
    ========================== */
    const recentBlogs = useMemo(() => {
        return blogData.slice(0, 10);
    }, [blogData]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search, activeCategory]);

    return (
        <div className="blog__standard section-padding">
            <div className="container">
                <div className="blog__header text-center mb-5">
                    <span className="subtitle">News & Insights</span>
                    <h4>Stories, Updates & Industry Insights</h4>
                </div>

                <div className="row">
                    {/* ===== BLOG LIST ===== */}
                    <div className="col-lg-8">
                        <div className="blog__standard-left">
                            {currentBlogItems.map((item) => (
                                <div
                                    key={item.id}
                                    className="blog__standard-item"
                                >
                                    {/* IMAGE */}
                                    <Link
                                        to={`/blog/${item.id}`}
                                        className="blog__standard-thumb"
                                    >
                                        <SafeImage
                                            isCoverImage
                                            src={item.images?.[0]}
                                            alt={item.title}
                                        />
                                    </Link>

                                    {/* CONTENT */}
                                    <div className="blog__standard-content">
                                        {item.source && (
                                            <div className="blog__source">
                                                {item.source}
                                            </div>
                                        )}

                                        <h3>
                                            <Link to={`/blog/${item.id}`}>
                                                {item.title}
                                            </Link>
                                        </h3>

                                        <p className="blog__excerpt">
                                            {truncateHtmlText(item.summaryEn)}
                                        </p>

                                        <p className="blog__excerpt muted">
                                            {truncateHtmlText(item.summaryId)}
                                        </p>
                                        <Link
                                            to={`/blog/${item.id}`}
                                            className="blog__read-more"
                                        >
                                            Read More →
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* PAGINATION */}
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                setCurrentPage={setCurrentPage}
                                position={position || "left"}
                            />
                        )}
                    </div>

                    {/* ===== SIDEBAR ===== */}
                    <div className="col-lg-4 columns_sticky lg-mb-25">
                        <div className="sidebar">

                            {/* SEARCH */}
                            <div className="sidebar-widget">
                                <h5 className="sidebar-title">Search News</h5>
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search articles..."
                                    className="sidebar-search"
                                />
                            </div>

                            {/* RECENT */}
                            <div className="sidebar-widget">
                                <h5 className="sidebar-title">Recent News</h5>
                                {recentBlogs.map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/blog/${item.id}`}
                                        className="sidebar-post"
                                    >
                                        <p>{item.title}</p>
                                    </Link>
                                ))}
                            </div>

                            {/* CATEGORIES */}
                            <div className="sidebar-widget">
                                <h5 className="sidebar-title">Categories</h5>
                                <div className="sidebar-tags">
                                    <span
                                        className={`sidebar-tag ${
                                            !activeCategory ? "active" : ""
                                        }`}
                                        onClick={() => setActiveCategory(null)}
                                    >
                                        All
                                    </span>

                                    {categories.map((tag) => (
                                        <span
                                            key={tag}
                                            className={`sidebar-tag ${
                                                activeCategory === tag
                                                    ? "active"
                                                    : ""
                                            }`}
                                            onClick={() => setActiveCategory(tag)}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="sidebar-widget sidebar-cta">
                                <span className="cta-subtitle">
                                    INSIGHTS & PARTNERSHIP
                                </span>

                                <h5>Looking for a Reliable Partner?</h5>

                                <p>
                                    Temukan bagaimana keahlian engineering dan kapabilitas
                                    dukungan proyek kami dapat memperkuat operasional Anda.
                                </p>

                                <Link
                                    to="/contact-us"
                                    className="build_button w-100"
                                >
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogStandardMain;
